import { useQueryClient } from "@tanstack/react-query";
import { useState } from "react";
import { Link } from "react-router-dom";
import { Badge, Button } from "reactstrap";
import useUpdateUserCloudPermissions from "../../../../api/api-hooks/auth/useUpdateUserCloudPermissions";
import {
  PeopleWithInfo,
  TenantWithPeopleInfo,
} from "../../../../api/api-hooks/cloud/useCloud";
import useRemoveUser from "../../../../api/api-hooks/tenantUsers/useRemoveUser";
import InviteUserDrawer from "../../../../components/Drawers/InviteUserDrawer";
import ManageUserPermissionsDrawer from "../../../../components/Drawers/ManageUserPermissionsDrawer";
import IconButton from "../../../../elements/IconButton";
import { PermissionProfile } from "../../../../types/GlobalInterfaces";
import { callFunctionIfExists } from "../../../../utils/GeneralUtils";
import {
  getUserCloudPermissionID,
  getUserCloudPermissions,
} from "../../../../utils/UserUtils";
import BasicTableView from "../../CloudListView/BasicTableView";
import BulkAddUsersDrawer from "./BulkAddUsersDrawer";

interface Props {
  cloud: TenantWithPeopleInfo;
  cloudPermissions: PermissionProfile[];
  loading: boolean;
  urlStart: string;
}

const CloudUsers = ({ cloud, cloudPermissions, loading, urlStart }: Props) => {
  const {
    updateUserCloudPermissions,
    updatingPermissions,
    updatePermissionsError,
  } = useUpdateUserCloudPermissions();
  const { removeUser } = useRemoveUser();
  const [manageUser, setManageUser] = useState<PeopleWithInfo | null>(null);
  const queryClient = useQueryClient();
  const cID = cloud?.id;

  const [addUserOpen, setAddUserOpen] = useState(false);
  const [bulkAddUsersOpen, setBulkAddUsersOpen] = useState(false);

  return (
    <>
      <div className="tw:mb-6 tw:flex tw:justify-end">
        <Button
          color="primary"
          size="sm"
          onClick={() => setBulkAddUsersOpen(true)}
        >
          Bulk Add Users
        </Button>
      </div>
      <BasicTableView
        loading={loading}
        items={cloud?.People ?? []}
        title="Users"
        getItemTitle={(u) => {
          const profile: any = getUserCloudPermissions(
            u,
            cloudPermissions,
            cID
          );

          return (
            <div className="d-flex align-items-center">
              <Link to={`${urlStart}/users/${u.PersonID}`}>
                {u.Name ? u.Name : u.PrimaryContactEmail}
              </Link>
              {!!profile?.Name && (
                <Badge
                  color={u.TenantOwner ? "secondary" : "info"}
                  className="ml-2"
                >
                  {profile?.Name}
                </Badge>
              )}
            </div>
          );
        }}
        getItemSubtitle={(u) =>
          u.Name ? u.PrimaryContactEmail : "Pending Invitation"
        }
        containerCSS=" mb-3 mt-2"
        titleCSS=" tw:rounded-t-md text-gray"
        CustomRenderer={(user) => {
          return (
            <div className="d-flex" onClick={(e) => e.stopPropagation()}>
              <IconButton
                icon="pencil"
                tooltipText={"Edit user's permissions"}
                onClick={() => setManageUser(user)}
              />
              <IconButton
                icon="close"
                color="danger"
                tooltipText={"Remove user from Tenant"}
                onClick={() => removeUserFromCloud(user)}
              />
            </div>
          );
        }}
      />
      <BulkAddUsersDrawer
        open={bulkAddUsersOpen}
        onClose={() => setBulkAddUsersOpen(false)}
        cloudID={cloud?.id || ""}
      />
      <InviteUserDrawer
        open={addUserOpen}
        onClose={() => setAddUserOpen(false)}
        permissions={cloudPermissions}
        onSuccess={() =>
          queryClient.invalidateQueries({
            queryKey: ["cloud", cloud?.id],
          })
        }
        supportTargetCloudID={cloud?.id} // This IS a support request
      />
      <ManageUserPermissionsDrawer
        permissions={cloudPermissions}
        open={manageUser !== null}
        onClose={() => setManageUser(null)}
        selectedUser={manageUser}
        getPermissionID={(user) => getUserCloudPermissionID(user, cID)}
        type="Cloud"
        updatePermissions={(variables: any) => {
          const newOnSuccess = () => {
            callFunctionIfExists(variables?.onSuccess);
            queryClient.invalidateQueries({
              queryKey: ["cloud", cID],
            });
          };

          updateUserCloudPermissions({
            ...variables,
            cloudID: cID,
            onSuccess: newOnSuccess,
          });
        }}
        updatingPermissions={updatingPermissions}
        updatePermissionsError={updatePermissionsError}
        supportTargetCloudID={cID!} // Support Request - Provide Cloud ID!
      />
    </>
  );

  async function removeUserFromCloud(user: PeopleWithInfo) {
    removeUser({
      user,
      supportTargetCloudID: cloud?.id || "", // Support Request - Provide Cloud ID!
      onSuccess: () =>
        queryClient.invalidateQueries({
          queryKey: ["cloud", cloud?.id],
        }),
    });
  }
};

export default CloudUsers;
